import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../contexts/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';

// Datas vêm de colunas `date` (meia-noite UTC). Formatar pelos componentes UTC
// evita mostrar o dia anterior em GMT-3.
const dataBR = (d) => {
  if (!d) return '\u2014';
  const dt = new Date(d);
  if (isNaN(dt)) return '\u2014';
  return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// Período de referência: só dia e mês importam — o backend compara com o mesmo
// dia/mês nos N anos anteriores ao da campanha e ignora o ano. Gravamos o ano
// 2000 (bissexto, aceita 29/02) só como marcador; ele nunca aparece na tela.
const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const dmBR = (md) => (md ? md.split('-').reverse().join('/') : '');
const listaAnos = (anos) => (anos.length <= 1 ? String(anos[0] ?? '') : `${anos.slice(0, -1).join(', ')} e ${anos[anos.length - 1]}`);
const estiloSel = 'font-size:0.82rem;margin:0;padding:4px 6px;width:auto;display:inline-block';
const seletorDiaMesHtml = (id) => `
  <select id="${id}-dia" class="swal2-select" style="${estiloSel}">
    ${Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map(d => `<option value="${d}">${d}</option>`).join('')}
  </select>
  <select id="${id}-mes" class="swal2-select" style="${estiloSel}">
    ${MESES_CURTOS.map((n, i) => `<option value="${String(i + 1).padStart(2, '0')}">${n}</option>`).join('')}
  </select>`;

export default function CampanhasVendedor() {
  const { temPermissao } = useAuth();
  const navigate = useNavigate();
  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unidades, setUnidades] = useState([]);

  const carregar = () => {
    axios.get(`${API}/campanhas-vendedor`).then(res => setCampanhas(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(carregar, []);
  useEffect(() => {
    axios.get(`${API}/produtos/unidades`).then(res => setUnidades(res.data)).catch(() => setUnidades([]));
  }, []);

  const novaCampanha = async () => {
    const { value: formValues } = await Swal.fire({
      title: 'Nova Campanha de Premiação',
      html: `
        <div style="text-align:left;padding:0 10px;margin-bottom:8px">
          <label style="font-size:0.85rem;font-weight:600">Tipo de campanha:</label>
          <div style="display:flex;gap:12px;margin-top:6px;flex-wrap:wrap">
            <label style="font-size:0.82rem;display:flex;align-items:center;gap:4px;cursor:pointer">
              <input type="radio" name="sw-tipo" value="vendedor" checked> <b>Vendedor</b> <small style="color:#666">(top 1)</small>
            </label>
            <label style="font-size:0.82rem;display:flex;align-items:center;gap:4px;cursor:pointer">
              <input type="radio" name="sw-tipo" value="gerente"> <b>Gerente</b> <small style="color:#666">(loja toda)</small>
            </label>
            <label style="font-size:0.82rem;display:flex;align-items:center;gap:4px;cursor:pointer">
              <input type="radio" name="sw-tipo" value="quantidade"> <b>Por quantidade</b> <small style="color:#666">(R$ por unidade)</small>
            </label>
            <label style="font-size:0.82rem;display:flex;align-items:center;gap:4px;cursor:pointer">
              <input type="radio" name="sw-tipo" value="ranking"> <b>Top vendedores</b> <small style="color:#666">(ranking, sem meta)</small>
            </label>
          </div>
        </div>
        <input id="sw-nome" class="swal2-input" placeholder="Nome (ex: CAMPANHA AMANCO NOV/DEZ 2025)" style="font-size:0.9rem">
        <label style="display:block;text-align:left;font-size:0.8rem;margin:10px 10px 0">Período da campanha:</label>
        <div style="display:flex;gap:8px;padding:0 10px">
          <input id="sw-ini" class="swal2-input" type="date" style="font-size:0.85rem;margin:4px 0;flex:1">
          <input id="sw-fim" class="swal2-input" type="date" style="font-size:0.85rem;margin:4px 0;flex:1">
        </div>
        <div id="sw-meta-fields">
          <label style="display:block;text-align:left;font-size:0.8rem;margin:10px 10px 0">Período de referência (histórico da meta):</label>
          <label style="display:flex;align-items:center;gap:6px;font-size:0.8rem;padding:0 10px;margin-top:4px;cursor:pointer">
            <input type="checkbox" id="sw-ref-pers"> Usar período diferente do da campanha
          </label>
          <div id="sw-ref-campos" style="display:none;gap:6px;padding:0 10px;align-items:center;margin-top:4px">
            ${seletorDiaMesHtml('sw-ref-ini')}
            <span style="font-size:0.8rem;color:#666">a</span>
            ${seletorDiaMesHtml('sw-ref-fim')}
          </div>
          <div style="display:flex;gap:8px;padding:0 10px;align-items:center">
            <label style="font-size:0.8rem;flex:1;text-align:left">Média de quantos anos?</label>
            <select id="sw-anos" class="swal2-input" style="font-size:0.85rem;margin:4px 0;flex:1">
              <option value="1">1 ano (só o período)</option>
              <option value="2">2 anos (média)</option>
              <option value="3" selected>3 anos (média)</option>
            </select>
          </div>
          <div id="sw-ref-resumo" style="text-align:left;padding:0 10px;font-size:0.75rem;color:#5C3D52;margin-top:4px">
            <i class="bi bi-calendar-range"></i> Defina as datas da campanha para ver o período de comparação.
          </div>
          <input id="sw-perc" class="swal2-input" type="number" step="0.1" placeholder="% acima do campeão (ex: 20)" style="font-size:0.9rem">
        </div>
        <div id="sw-quant-fields" style="display:none">
          <label style="display:block;text-align:left;font-size:0.8rem;margin:10px 10px 0">Prêmio por unidade vendida (sem meta — paga por m² vendido):</label>
          <div style="display:flex;gap:8px;padding:0 10px">
            <input id="sw-valor-unid" class="swal2-input" type="number" step="0.01" placeholder="R$ por unidade (ex: 1)" style="font-size:0.85rem;margin:4px 0;flex:2">
            <select id="sw-unidade" class="swal2-input" style="font-size:0.85rem;margin:4px 0;flex:1">
              ${unidades.map(u => `<option value="${u.unidade}" ${u.unidade === 'M2' ? 'selected' : ''}>${u.unidade}</option>`).join('')}
            </select>
          </div>
          <div style="text-align:left;padding:0 10px;font-size:0.72rem;color:#888;margin-top:4px">
            <i class="bi bi-info-circle"></i> Só conta vendas na unidade selecionada (ex: M2). Produtos do filtro em outra unidade ficam de fora.
          </div>
        </div>
        <div id="sw-rank-fields" style="display:none">
          <div style="display:flex;gap:8px;padding:0 10px;align-items:center;margin-top:6px">
            <label style="font-size:0.8rem;flex:1;text-align:left">Rankear por:</label>
            <label style="font-size:0.82rem;flex:1"><input type="radio" name="sw-rankpor" value="valor" checked> Valor (R$)</label>
            <label style="font-size:0.82rem;flex:1"><input type="radio" name="sw-rankpor" value="quantidade"> Quantidade</label>
          </div>
          <div style="display:flex;gap:8px;padding:0 10px;align-items:center">
            <label style="font-size:0.8rem;flex:1;text-align:left">Quantos ganham (por loja)?</label>
            <input id="sw-qtdganha" class="swal2-input" type="number" min="1" max="20" value="3" style="font-size:0.85rem;margin:4px 0;flex:1">
          </div>
          <div style="text-align:left;padding:0 10px;font-size:0.72rem;color:#888;margin-top:4px">
            <i class="bi bi-info-circle"></i> Sem meta. Os prêmios em dinheiro de cada posição você define na tela da campanha (aba Configurações).
          </div>
        </div>
      `,
      focusConfirm: false, showCancelButton: true, confirmButtonText: 'Criar', cancelButtonText: 'Cancelar', confirmButtonColor: '#5C3D52',
      didOpen: () => {
        // Período de referência: por padrão acompanha a campanha (mesmo dia/mês, nos
        // anos anteriores). O resumo mostra exatamente com o que a meta vai comparar.
        const el = (id) => document.getElementById(id);
        const mdCampanha = (iso) => (iso ? iso.slice(5, 10) : '');
        const mdSeletor = (id) => `${el(id + '-mes').value}-${el(id + '-dia').value}`;
        const setSeletor = (id, md) => {
          if (!md) return;
          const [m, d] = md.split('-');
          el(id + '-mes').value = m;
          el(id + '-dia').value = d;
        };
        const atualizarResumo = () => {
          const pers = el('sw-ref-pers').checked;
          el('sw-ref-campos').style.display = pers ? 'flex' : 'none';
          const iniC = el('sw-ini').value;
          const fimC = el('sw-fim').value;
          if (!iniC || !fimC) {
            el('sw-ref-resumo').innerHTML = '<i class="bi bi-calendar-range"></i> Defina as datas da campanha para ver o período de comparação.';
            return;
          }
          const a = pers ? mdSeletor('sw-ref-ini') : mdCampanha(iniC);
          const b = pers ? mdSeletor('sw-ref-fim') : mdCampanha(fimC);
          const base = parseInt(iniC.slice(0, 4), 10) - 1;
          const anos = Array.from({ length: parseInt(el('sw-anos').value, 10) || 1 }, (_, i) => base - i);
          el('sw-ref-resumo').innerHTML = `<i class="bi bi-calendar-range"></i> Compara com ${dmBR(a)} a ${dmBR(b)}${b < a ? ' (termina no ano seguinte)' : ''} de ${listaAnos(anos)}.`;
        };
        // Ao ligar a personalização, parte do período da campanha pra ajustar.
        el('sw-ref-pers').addEventListener('change', () => {
          if (el('sw-ref-pers').checked) {
            setSeletor('sw-ref-ini', mdCampanha(el('sw-ini').value));
            setSeletor('sw-ref-fim', mdCampanha(el('sw-fim').value));
          }
          atualizarResumo();
        });
        ['sw-ini', 'sw-fim', 'sw-anos', 'sw-ref-ini-dia', 'sw-ref-ini-mes', 'sw-ref-fim-dia', 'sw-ref-fim-mes']
          .forEach(id => el(id).addEventListener('change', atualizarResumo));

        // Alterna campos meta x quantidade conforme o tipo escolhido
        const metaFields = document.getElementById('sw-meta-fields');
        const quantFields = document.getElementById('sw-quant-fields');
        const rankFields = document.getElementById('sw-rank-fields');
        const aplicarTipo = () => {
          const t = document.querySelector('input[name="sw-tipo"]:checked')?.value;
          const isQuant = t === 'quantidade';
          const isRank = t === 'ranking';
          metaFields.style.display = (isQuant || isRank) ? 'none' : '';
          quantFields.style.display = isQuant ? '' : 'none';
          rankFields.style.display = isRank ? '' : 'none';
        };
        document.querySelectorAll('input[name="sw-tipo"]').forEach(r => r.addEventListener('change', aplicarTipo));
      },
      preConfirm: () => {
        const tipo = document.querySelector('input[name="sw-tipo"]:checked')?.value || 'vendedor';
        // Referência em dia/mês (ano 2000 como marcador). Sem personalização, é o
        // próprio período da campanha.
        const pers = document.getElementById('sw-ref-pers').checked;
        const mdSel = (id) => `${document.getElementById(id + '-mes').value}-${document.getElementById(id + '-dia').value}`;
        const iniC = document.getElementById('sw-ini').value;
        const fimC = document.getElementById('sw-fim').value;
        const refIniMD = pers ? mdSel('sw-ref-ini') : (iniC ? iniC.slice(5, 10) : '');
        const refFimMD = pers ? mdSel('sw-ref-fim') : (fimC ? fimC.slice(5, 10) : '');
        return {
          tipo,
          nome: document.getElementById('sw-nome').value,
          data_inicio: document.getElementById('sw-ini').value || null,
          data_fim: document.getElementById('sw-fim').value || null,
          periodo_ref_inicio: refIniMD ? `2000-${refIniMD}` : null,
          periodo_ref_fim: refFimMD ? `2000-${refFimMD}` : null,
          qtd_anos_ref: parseInt(document.getElementById('sw-anos').value) || 1,
          percentual_meta: parseFloat(document.getElementById('sw-perc').value) || 20,
          valor_por_unidade: tipo === 'quantidade' ? (parseFloat(document.getElementById('sw-valor-unid').value) || 0) : null,
          unidade_premiada: tipo === 'quantidade' ? ((document.getElementById('sw-unidade').value || 'M2').trim().toUpperCase()) : null,
          rankear_por: tipo === 'ranking' ? (document.querySelector('input[name="sw-rankpor"]:checked')?.value || 'valor') : null,
          qtd_ganhadores: tipo === 'ranking' ? (parseInt(document.getElementById('sw-qtdganha').value) || 3) : null,
        };
      },
    });
    if (!formValues?.nome) return;
    try {
      const res = await axios.post(`${API}/campanhas-vendedor`, formValues);
      navigate(`/campanhas-vendedor/${res.data.id}`);
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro ao criar', 'error');
    }
  };

  const remover = async (id, nome) => {
    const r = await Swal.fire({ title: `Remover "${nome}"?`, text: 'Só rascunhos podem ser removidos', icon: 'warning', showCancelButton: true, confirmButtonText: 'Remover', cancelButtonText: 'Cancelar', confirmButtonColor: '#dc3545' });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/campanhas-vendedor/${id}`);
      carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Erro', 'error');
    }
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border" style={{ color: '#6B2C5C' }} /></div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0"><i className="bi bi-trophy me-2"></i>Campanhas de Premiação</h4>
        {temPermissao('campanhas_vendedor') && (
          <button className="btn-primary-mkt" onClick={novaCampanha}>
            <i className="bi bi-plus-lg me-1"></i> Nova Campanha
          </button>
        )}
      </div>

      <div className="card p-3">
        <div className="tabela-container">
          <table className="table table-mkt table-hover mb-0">
            <thead>
              <tr><th>Nome</th><th>Tipo</th><th>Fornecedor</th><th>Período</th><th>Classes</th><th>Grupos</th><th>% Meta</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {campanhas.map(c => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/campanhas-vendedor/${c.id}`)}>
                  <td className="fw-semibold">
                    {c.nome}
                    {!c.visivel_gerente && <i className="bi bi-eye-slash ms-2" style={{ color: '#999' }} title="Oculta pros gerentes de loja"></i>}
                  </td>
                  <td>
                    {c.tipo === 'gerente'
                      ? <span className="badge" style={{ background: '#4A1F40', color: '#fff', fontSize: '0.7rem' }}><i className="bi bi-person-badge me-1"></i>Gerente</span>
                      : c.tipo === 'quantidade'
                      ? <span className="badge" style={{ background: '#1F5C4A', color: '#fff', fontSize: '0.7rem' }}><i className="bi bi-rulers me-1"></i>Quantidade</span>
                      : c.tipo === 'ranking'
                      ? <span className="badge" style={{ background: '#8a6d00', color: '#fff', fontSize: '0.7rem' }}><i className="bi bi-trophy me-1"></i>Top vend.</span>
                      : <span className="badge" style={{ background: '#6B2C5C', color: '#fff', fontSize: '0.7rem' }}><i className="bi bi-person me-1"></i>Vendedor</span>}
                  </td>
                  <td>{c.fornecedor || '—'}</td>
                  <td style={{ fontSize: '0.83rem' }}>
                    {dataBR(c.data_inicio)}
                    {' → '}
                    {dataBR(c.data_fim)}
                  </td>
                  <td>{c.qtd_classes || 0}</td>
                  <td>{c.qtd_grupos || 0}</td>
                  <td>{c.tipo === 'quantidade'
                    ? (c.valor_por_unidade ? `R$ ${parseFloat(c.valor_por_unidade).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/${(c.unidade_premiada || 'un').toLowerCase()}` : '—')
                    : c.tipo === 'ranking'
                    ? `Top ${c.qtd_ganhadores || 3} · ${c.rankear_por === 'quantidade' ? 'qtd' : 'R$'}`
                    : (c.percentual_meta ? `${c.percentual_meta}%` : '—')}</td>
                  <td><span className={`badge-${c.status || 'rascunho'}`}>{c.status}</span></td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); navigate(`/campanhas-vendedor/${c.id}`); }} title="Abrir">
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      {c.status === 'rascunho' && temPermissao('campanhas_vendedor') && (
                        <button className="btn btn-sm btn-outline-danger" onClick={(e) => { e.stopPropagation(); remover(c.id, c.nome); }} title="Remover">
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {campanhas.length === 0 && <tr><td colSpan="9" className="text-center text-muted">Nenhuma campanha cadastrada</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
